import { ColorPicker, Form, Select } from "antd";
import type { HTMLAttributes } from "react";
import { Fragment } from "react/jsx-runtime";
import { useIntl } from "react-intl";
import { FormCheckbox } from "~components/Form/FormCheckbox";
import { FormHelpSection, type FormSectionLabel } from "~components/Form/FormHelpSection";
import { FormInput } from "~components/Form/FormInput";
import { FormItem } from "~components/Form/FormItem";
import { FormSelect } from "~components/Form/FormSelect";
import { FormSelectYesNo } from "~components/Form/FormSelectYesNo";
import { SelectUniform } from "~components/SelectUniform/SelectUniform";
import { FixtureResultStatuses } from "~enums/fixtureResultStatuses";
import { UniformTypes } from "~enums/uniformTypes";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { useGetFixture } from "~pages/Fixtures/hooks";
import type { EditFixtureForm, EditFixtureFormKeys, FixtureEntityTransformed, FixturePersonTransformed } from "~pages/Fixtures/types";
import { useExternalIdValidator } from "../../hooks";
import { StyledEntrant, StyledEntrantName } from "../../styles";
import styles from "./FixtureCompetitorsSection.module.css";

type Props = HTMLAttributes<HTMLDivElement> & {
  isUpdating: boolean;
  isHome?: boolean;
};

export const FixtureCompetitorsSection = ({ isUpdating, isHome = false, ...props }: Props) => {
  const form = Form.useFormInstance<EditFixtureForm>();
  const { formatMessage } = useIntl();

  const { uniforms } = useSportConfiguration();
  const isUniformEnabled = Object.entries(uniforms).length > 0;

  const { data: fixtureData, isFetching } = useGetFixture();
  const competitorsKey: EditFixtureFormKeys = "competitors";
  const externalIdRule = useExternalIdValidator(competitorsKey);

  if (!fixtureData) return null;

  const competitors: (FixtureEntityTransformed & { index: number })[] = fixtureData.formData.competitors
    .map((x, index) => ({ ...x, index }))
    .filter((x) => x.isHome === isHome);

  const fields: FormSectionLabel[] = [
    {
      titleIntlKey: "fixture.competitor.name",
      descriptionIntlKey: "form.help.fixture.competitor.name",
    },
    {
      titleIntlKey: "fixture.competitor.abbreviation",
      descriptionIntlKey: "form.help.fixture.competitor.abbreviation",
    },
    {
      titleIntlKey: "resultStatus",
      descriptionIntlKey: "form.help.fixture.resultStatus",
    },
    {
      titleIntlKey: "uniform",
      descriptionIntlKey: "form.help.fixture.uniform",
    },
    {
      titleIntlKey: "externalId",
      descriptionIntlKey: "form.help.fixture.externalId",
    },
    {
      titleIntlKey: "fixture.competitor.colors",
      descriptionIntlKey: "form.help.fixture.competitor.colors",
    },
    {
      titleIntlKey: "includeInRepresentation",
      descriptionIntlKey: "form.help.fixture.competitor.includeInRepresentation",
    },
  ];

  const colorPickerSharedProps = {
    open: false,
    disabled: isUpdating || isFetching,
    className: styles.colorPicker,
  };

  const sectionTitle = formatMessage(
    {
      id: isHome ? "fixture.update.competitor.home.details" : "fixture.update.competitor.away.details",
    },
    { count: competitors.length ?? 0 },
  );

  return competitors.length ? (
    <div {...props}>
      <FormHelpSection sectionTitle={sectionTitle} modalTitle={sectionTitle} fields={fields} />
      <StyledEntrant>
        {competitors.map((competitor) => (
          <Fragment key={competitor.entityId}>
            <div grid-full-width="true">
              <StyledEntrantName>{competitor.name}</StyledEntrantName>
            </div>
            <FormCheckbox<EditFixtureForm> hidden name={[competitorsKey, competitor.index, "isHome"]} />
            <FormInput<EditFixtureForm> hidden name={[competitorsKey, competitor.index, "entityId"]} />
            <FormInput<EditFixtureForm>
              label={formatMessage({ id: "fixture.competitor.name" })}
              name={[competitorsKey, competitor.index, "name"]}
              disabled
            />
            <FormInput<EditFixtureForm>
              label={formatMessage({
                id: "fixture.competitor.abbreviation",
              })}
              name={[competitorsKey, competitor.index, "nameAbbreviation"]}
              disabled
            />
            <FormSelect<EditFixtureForm>
              name={[competitorsKey, competitor.index, "resultStatus"]}
              label={formatMessage({ id: "resultStatus" })}
              disabled={isUpdating || isFetching}
            >
              {Object.values(FixtureResultStatuses).map((status) => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </FormSelect>
            <FormInput<EditFixtureForm>
              label={formatMessage({ id: "externalId" })}
              name={[competitorsKey, competitor.index, "externalId"]}
              disabled={isUpdating || isFetching}
              rules={externalIdRule}
              inputProps={{
                onChange: () => {
                  // trigger validation for all externalIds (fixes bug when changed one externalId and another one is keep being invalid)
                  const formCompetitors: FixturePersonTransformed[] = form.getFieldValue(competitorsKey);
                  const allExternalIdFields = formCompetitors.map((_, idx) => [competitorsKey, idx, "externalId"]);
                  form.validateFields(allExternalIdFields);
                },
              }}
            />
            {isUniformEnabled && (
              <FormItem<EditFixtureForm>
                layout="vertical"
                label={formatMessage({ id: "uniform" })}
                name={[competitorsKey, competitor.index, "uniform"]}
              >
                <SelectUniform baseId={competitor.entityId} uniformType={UniformTypes.Entity} disabled={isUpdating || isFetching} />
              </FormItem>
            )}
            <div>
              <label htmlFor={`colors-${competitor.index}`} className={styles.mainLabel}>
                {formatMessage({ id: "fixture.competitor.colors" })}
              </label>
              <div id={`colors-${competitor.index}`} className={styles.fixtureCompetitorColors}>
                <FormItem<EditFixtureForm> layout="horizontal" hideLabel name={[competitorsKey, competitor.index, "colors", "primary"]}>
                  <ColorPicker showText={() => formatMessage({ id: "primary" })} {...colorPickerSharedProps} />
                </FormItem>

                <FormItem<EditFixtureForm> layout="horizontal" hideLabel name={[competitorsKey, competitor.index, "colors", "secondary"]}>
                  <ColorPicker showText={() => formatMessage({ id: "secondary" })} {...colorPickerSharedProps} />
                </FormItem>

                <FormItem<EditFixtureForm> layout="horizontal" hideLabel name={[competitorsKey, competitor.index, "colors", "tertiary"]}>
                  <ColorPicker showText={() => formatMessage({ id: "tertiary" })} {...colorPickerSharedProps} />
                </FormItem>
              </div>
            </div>
            <FormSelectYesNo<EditFixtureForm>
              label={formatMessage({ id: "includeInRepresentation" })}
              name={[competitorsKey, competitor.index, "includeInRepresentation"]}
              disabled={isUpdating || isFetching}
            />
          </Fragment>
        ))}
      </StyledEntrant>
    </div>
  ) : (
    <div />
  );
};
